import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faChevronDown,
  faChevronRight
} from '@fortawesome/free-solid-svg-icons';

export interface MenuItemChild {
  label: string;
  route?: string;
  iconPath?: string;
  /** Nested sub-menu (e.g. Batch Admin -> Charge Batch Admin, Payment Batch Admin) */
  children?: MenuItemChild[];
}

export interface MenuItem {
  label: string;
  route?: string;
  iconPath: string;
  children?: MenuItemChild[];
  alwaysActive?: boolean;
  /** When true, only one first-level child submenu can be open at a time (accordion behavior). */
  accordionChildren?: boolean;
}

export const SIDEBAR_MENU_ITEMS: MenuItem[] = [
  {
    label: 'Dashboard',
    iconPath: 'assets/images/icons/dashboard-icon.svg',
    route: '/dashboard',
    alwaysActive: true
  },
  {
    label: 'Daily Roster',
    iconPath: 'assets/images/icons/daily-roster-icon.svg',
    route: '/daily-roaster'
  },
  {
    label: 'Patient',
    iconPath: 'assets/images/icons/patient-icon.svg',
    route: '/patient'
  },
  {
    label: 'Scheduler',
    iconPath: 'assets/images/icons/scheduler-icon.svg',
    route: '/scheduler',
    children: [
      {
        label: 'Schedule',
        route: '/scheduler/schedule'
      },
      {
        label: 'Create',
        route: '/scheduler/create'
      },
    ]
  },
  {
    label: 'Utility',
    iconPath: 'assets/images/icons/utility-icon.svg',
    route: '/utility',
    children: [
      {
        label: 'Patient Recall Report',
        route: '/scheduler/recall-list'
      }
    ]
  },
  {
    label: 'Master',
    iconPath: 'assets/images/icons/utility-icon.svg',
    route: '/master',
    accordionChildren: true,
    children: [
      {
        label: 'Code Management',
        children: [
          { label: 'Diagnosis Category', route: '/master/code-management/diagnosis-category' },
          { label: 'Diagnosis', route: '/master/code-management/diagnosis' },
          { label: 'Procedure', route: '/master/code-management/procedure' },
          { label: 'Modifier', route: '/master/code-management/modifier' },
          { label: 'Place of Service', route: '/master/code-management/place-of-service' },
          { label: 'Type Of Service', route: '/master/code-management/type-of-service' },
          { label: 'Drugs', route: '/master/code-management/drugs' },
          { label: 'Allergen', route: '/master/code-management/allergen' }
        ]
      },
      {
        label: 'Entity Management',
        children: [
          { label: 'Practice', route: '/master/entity-management/practice' },
          { label: 'Location', route: '/master/entity-management/location' },
          { label: 'Provider', route: '/master/entity-management/provider' },
          { label: 'Lawyer', route: '/master/entity-management/lawyer' },
          { label: 'Vendors', route: '/master/entity-management/vendors' }
        ]
      },
      {
        label: 'Others',
        children: [
          { label: 'Visit Type', route: '/master/others/visit-type' },
          { label: 'Reason', route: '/master/others/reason' },
          { label: 'Comments', route: '/master/others/comments' },
          { label: 'Holiday Definition', route: '/master/others/holiday-definition' },
          { label: "Patient's Education Library", route: '/master/others/patients-education-library' },
          { label: 'Payment Ledger', route: '/master/others/payment-ledger' },
          { label: 'Message Master', route: '/master/others/message-master' },
          { label: 'Remit Code', route: '/master/others/remit-code' }
        ]
      },
      {
        label: 'Insurance Management',
        children: [
          { label: 'Insurance', route: '/master/insurance-management/insurance' },
          { label: 'Plan Category', route: '/master/insurance-management/plan-category' },
          { label: 'Plan', route: '/master/insurance-management/plan' }
        ]
      },
      {
        label: 'Security',
        children: [
          { label: 'Users', route: '/master/security/users' },
          { label: 'User Permission', route: '/master/security/user-role' },
          { label: 'User Role', route: '/master/security/user-roles' },
          { label: 'EMR Permission', route: '/master/security/emr-permission' },
          { label: 'User Security', route: '/master/security/user-security' },
          { label: 'Kill User Session', route: '/master/security/kill-user-session' }
        ]
      },
      {
        label: 'Follow-Up Profile',
        children: [
          { label: 'Followup Reason', route: '/master/follow-up-profile/followup-reason' },
          { label: 'Followup Action', route: '/master/follow-up-profile/followup-action' },
          { label: 'Followup Call Type', route: '/master/follow-up-profile/followup-call-type' },
          { label: 'Followup Type', route: '/master/follow-up-profile/followup-type' },
          { label: 'Followup All Visits', route: '/master/follow-up-profile/followup-all-visits' },
          { label: 'Followup Call Center', route: '/master/follow-up-profile/followup-call-center' }
        ]
      },
      { label: 'Patient Messages', route: '/master/patient-messages' },
      { label: 'To Do List', route: '/master/to-do-list' },
      { label: 'Show System Options', route: '/master/show-system-options' },
      { label: 'Appointment Color', route: '/master/appointment-status-color' }
    ]
  },
  {
    label: 'Batch',
    iconPath: 'assets/images/icons/batch-icon.svg',
    route: '/batch',
    children: [
      {
        label: 'Batch Admin',
        children: [
          { label: 'Charge Batch Admin', route: '/batch/charge-batchadmin' },
          { label: 'Payment Batch Admin', route: '/batch/payment-batchadmin' },
          { label: 'Denial Batch Admin', route: '/batch/denial-batchadmin' }
        ]
      },
      { label: 'Claim Editing', route: '/batch/claim-editing' },
      {
        label: 'Charges',
        children: [
          { label: 'Charge Entry', route: '/batch/charge-entry' },
          { label: 'Ticket Charge Entry', route: '/batch/ticket-charge-entry' },
          { label: 'Charge Listing', route: '/batch/charge-listing' }
        ]
      },
      {
        label: 'Submit',
        children: [
          { label: 'Paper Submit', route: '/batch/paper-submit' },
          { label: 'EDI', route: '/batch/electronic-submit' },
          { label: 'Batch Submit Log', route: '/batch/batch-submit-log' },
          { label: 'EDI Report Log', route: '/batch/edi-report-log' }
        ]
      },
      {
        label: 'Payment',
        children: [
          { label: 'Payment Management', route: '/batch/payment-management' },
          { label: 'ERA File Process', route: '/batch/era-file-process' }
        ]
      },
      {
        label: 'Printing',
        children: [
          { label: 'Statement Printing', route: '/batch/statement-printing' },
          { label: 'WCNF Printing', route: '/batch/wcnf-printing' }
        ]
      },
      {
        label: 'Viewer',
        children: [
          { label: 'Payment Document Viewer', route: '/batch/payment-document-viewer' }
        ]
      }
    ]
  },
  {
    label: 'Follow Up',
    iconPath: 'assets/images/icons/follow-up-icon.svg',
    children: [
      {
        label: 'Plan Follow Up',
        route: '/follow-up/plan-follow-up'
      },
      {
        label: 'Patient Follow Up',
        route: '/follow-up/patient-follow-up'
      }
    ]
  },
  {
    label: 'Reports',
    iconPath: 'assets/images/icons/reports-icon.svg',
    route: '/reports',
    children: [
      {
        label: 'Patient Reports',
        children: [
          { label: 'Patients List', route: '/reports/patient-reports/patients-list' },
          { label: 'Patient Insured Party', route: '/reports/patient-reports/patient-insured-party' },
          { label: 'Patient No-Show Report', route: '/reports/patient-didnot-showup' },
          { label: 'End of day', route: '/reports/patient-reports/end-of-day' },

          // Archived: additional Patient Reports (uncomment to restore in menu)
          // { label: 'Patient Responsibility', route: '/reports/patient-reports/patient-responsibility' },
          // { label: 'Advance Payment', route: '/reports/patient-reports/advance-payment' },
          // { label: 'Patient Summary Analysis', route: '/reports/patient-reports/patient-summary-analysis' },
          // { label: 'Patient Financial Analysis', route: '/reports/patient-reports/patient-financial-analysis' },
          // { label: 'Patient Co Pay', route: '/reports/patient-reports/patient-co-pay' },
          // { label: 'Patient A/R By Visits', route: '/reports/patient-reports/patient-ar-by-visits' },
          // { label: 'Patient Diagnosis Analysis', route: '/reports/patient-reports/patient-diagnosis-analysis' },
          // { label: 'Patient Procedure Analysis', route: '/reports/patient-reports/patient-procedure-analysis' },
        ]
      },
      {
        label: 'Management Reports',
        route: '/reports/management-reports',
        children: [
          { label: 'Provider Analysis By Plan', route: '/reports/management-reports/provider-analysis-by-plan' },
          {
            label: 'Provider Performance',
            children: [
              { label: 'Charges', route: '/reports/management-reports/provider-performance/charges' },
              { label: 'Charge Summary', route: '/reports/management-reports/provider-performance/charge-summary' },
              { label: 'Payment', route: '/reports/management-reports/provider-performance/payment' },
              { label: 'Payment Summary', route: '/reports/management-reports/provider-performance/payment-summary' }
            ]
          },
          {
            label: 'Provider Procedure Utilization',
            route: '/reports/management-reports/provider-procedure-utilization'
          },
          { label: 'Procedure-wise Paid Report', route: '/reports/management-reports/procedure-wise-paid' },
          {
            label: 'Provider Monthly Summary',
            route: '/reports/management-reports/revenue/provider-monthly-summary'
          },
          {
            label: 'Monthly Billing Summary Test',
            route: '/reports/management-reports/revenue/monthly-billing-summary'
          },
          { label: 'Collection Summary', route: '/reports/management-reports/collection-summary' },
          { label: 'Statement Summary', route: '/reports/management-reports/statement-history' },
          { label: 'Charge List', route: '/reports/management-reports/charge-list' },
          { label: 'Transaction By Entry Date', route: '/reports/management-reports/transaction-by-entry-date' },

          // Archived: additional Management Reports (uncomment to restore in menu)
          // { label: 'By Plan', route: '/reports/management-reports/by-plan' },
          // {
          //   label: 'Revenue',
          //   children: [
          //     { label: 'Monthly Billing Summary', route: '/reports/management-reports/revenue/monthly-billing-summary' },
          //     { label: 'Payor Mix Summary', route: '/reports/management-reports/revenue/payor-mix-summary' },
          //     { label: 'Monthly Deliveries', route: '/reports/management-reports/revenue/monthly-deliveries' },
          //   ],
          // },
          // { label: 'Enterprise Aging By DOS', route: '/reports/management-reports/enterprise-aging-by-dos' },
          // { label: 'Referring Provider Productivity', route: '/reports/management-reports/referring-provider-productivity' },
          // { label: 'User Productivity', route: '/reports/management-reports/user-productivity' },
          // { label: 'Copay by User', route: '/reports/management-reports/copay-by-user' },
          // { label: 'A/R Analysis By Location', route: '/reports/management-reports/ar-analysis-by-location' },
          // { label: 'A/R Analysis By Provider', route: '/reports/management-reports/ar-analysis-by-provider' },
          // { label: 'A/R Analysis By Insurance', route: '/reports/management-reports/ar-analysis-by-insurance' },
          // { label: 'A/R Analysis By Plan category', route: '/reports/management-reports/ar-analysis-by-plan-category' },
          // { label: 'A/R Analysis By Plan Category Crosstab', route: '/reports/management-reports/ar-analysis-by-plan-category-crosstab' },
          // {
          //   label: 'A/R Analysis By Year/ Month Plan Category Crosstab',
          //   route: '/reports/management-reports/ar-analysis-by-year-month-plan-category-crosstab',
          // },
          // { label: 'A/R Analysis by Enterprise', route: '/reports/management-reports/ar-analysis-by-enterprise' },
          // { label: 'A/R Analysis By Location Provider', route: '/reports/management-reports/ar-analysis-by-location-provider' },
        ]
      },
      {
        label: 'Schedule Reports',
        route: '/reports/schedule-reports',
        children: [
          { label: 'Provider Appointments', route: '/reports/schedule-reports/provider-appointment' },
          { label: 'Daily Confirmation', route: '/reports/schedule-reports/daily-confirmation' },
          { label: 'Appointment Status List', route: '/reports/schedule-reports/appointment-status-list' },

          // Archived: additional Schedule Reports (uncomment to restore in menu)
          // { label: 'Provider Booking Analysis', route: '/reports/schedule-reports/provider-booking-analysis' },
          // { label: 'Provider Appointment Analysis', route: '/reports/schedule-reports/provider-appointment-analysis' },
        ]
      },
      {
        label: 'Plan Reports',
        route: '/reports/plan-reports',
        children: [
          { label: 'Plan Outstanding Balance', route: '/reports/plan-reports/plan-outstanding-balance' },
          { label: 'Payment Ledger', route: '/reports/plan-reports/payment-ledger' },
          {
            label: 'Diagnosis Procedure Report',
            children: [
              { label: 'By Plan', route: '/reports/plan-reports/diagnosis-procedure/by-plan' },
              { label: 'By Patient', route: '/reports/plan-reports/diagnosis-procedure/by-patient' },
              { label: 'Visitwise Payment Report', route: '/reports/plan-reports/diagnosis-procedure/visitwise-payment-report' }
            ]
          },

          // Archived: additional Plan Reports only
          // { label: 'Plan A/R By Visits', route: '/reports/plan-reports/plan-ar-by-visits' },
          // { label: 'Plan A/R By DOS', route: '/reports/plan-reports/plan-ar-by-dos' },
          // { label: 'Plan Overpayment', route: '/reports/plan-reports/plan-overpayment' },
          // {
          //   label: 'Diagnosis Procedure',
          //   children: [
          //     { label: 'By Plan', route: '/reports/plan-reports/diagnosis-procedure/by-plan' },
          //     { label: 'By Patient', route: '/reports/plan-reports/diagnosis-procedure/by-patient' },
          //     { label: 'Visitwise Payment Report', route: '/reports/plan-reports/diagnosis-procedure/visitwise-payment-report' },
          //   ],
          // },
          // { label: 'Plan Procedure Utilization', route: '/reports/plan-reports/plan-procedure-utilization' },
          // { label: 'Plan Procedure Allowed Variance', route: '/reports/plan-reports/plan-procedure-allowed-variance' },
          // { label: 'Plan 100 Percent Writeoff', route: '/reports/plan-reports/plan-100-percent-writeoff' },
          // {
          //   label: 'Plan Payment Analysis of Submitted Charges',
          //   route: '/reports/plan-reports/plan-payment-analysis',
          // },
          // { label: 'Plan Aging', route: '/reports/plan-reports/plan-aging' },
        ]
      },
      {
        label: 'Follow Up Report',
        route: '/reports/follow-up-reports',
        children: [
          {
            label: 'Follow-up Bucket',
            children: [
              { label: 'Follow-up Bucket Detail', route: '/reports/plan-reports/follow-up-bucket-detail' },
              { label: 'Group Summary', route: '/reports/follow-up-reports/follow-up-bucket/group-summary' },
              { label: 'Action Summary', route: '/reports/follow-up-reports/follow-up-bucket/action-summary' },
              {
                label: "What's Outstanding and WHY",
                route: '/reports/follow-up-reports/follow-up-bucket/whats-outstanding-and-why'
              }
            ]
          }
        ]
      }

      // Archived: former top-level Reports menu groups (uncomment block(s) and add trailing comma on the last Reports child `},` above)
      // {
      //   label: 'Follow Up Reports',
      //   route: '/reports/follow-up-reports',
      //   children: [
      //     {
      //       label: 'Follow Up Bucket',
      //       children: [
      //         { label: 'Detail', route: '/reports/follow-up-reports/follow-up-bucket/detail' },
      //         { label: 'Group Summary', route: '/reports/follow-up-reports/follow-up-bucket/group-summary' },
      //         { label: 'Action Summary', route: '/reports/follow-up-reports/follow-up-bucket/action-summary' },
      //         {
      //           label: "What's Outstanding and WHY",
      //           route: '/reports/follow-up-reports/follow-up-bucket/whats-outstanding-and-why',
      //         },
      //       ],
      //     },
      //     { label: 'Denial Reason Activity', route: '/reports/follow-up-reports/denial-reason-activity' },
      //   ],
      // },
      // {
      //   label: 'Other Reports',
      //   route: '/reports/other-reports',
      //   children: [
      //     { label: 'Patient Messages', route: '/reports/other-reports/patient-messages' },
      //     { label: 'To Do List', route: '/reports/other-reports/to-do-list' },
      //     { label: 'Collection Reports', route: '/reports/other-reports/collection-reports' },
      //   ],
      // },
      // { label: 'Audit Report', route: '/reports/audit-report' },
      // { label: 'Procedure Day Sheet', route: '/reports/procedure-day-sheet' },
      // { label: 'Patient Didnot Showup', route: '/reports/patient-didnot-showup' },
      // { label: 'MDVIP Patient Data Export', route: '/reports/mdvip-patient-data-export' },
    ]
  }
];

export const SIDEBAR_ICONS = {
  chevronDown: faChevronDown,
  chevronRight: faChevronRight
};
